import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { uploadXlsx, getUploads } from "../../lib/api";
import type { XlsxUpload, UploadResult } from "../../lib/types";

function formatTs(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function UploadTab() {
  const { t } = useAuth();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<XlsxUpload[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  async function reloadHistory() {
    setLoadingHistory(true);
    try {
      const { uploads } = await getUploads();
      setHistory(uploads);
    } catch { /* non-fatal */ }
    finally { setLoadingHistory(false); }
  }

  useEffect(() => { reloadHistory(); }, []);

  async function submit() {
    if (!file) return;
    setError(null);
    setResult(null);
    setSubmitting(true);
    try {
      const res = await uploadXlsx(file);
      setResult(res);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await reloadHistory();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="p-4 space-y-4">
      <div className="bg-white rounded-2xl p-4 border border-slate-200 space-y-3">
        <div className="text-sm font-semibold text-slate-800">{t("upload_title")}</div>

        <label className="block">
          <span className="text-xs text-slate-500">{t("upload_pick")}</span>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full mt-1 text-xs text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
          />
        </label>

        {file && (
          <div className="text-xs text-slate-600">
            <b>{file.name}</b> ({(file.size / 1024).toFixed(1)} KB)
          </div>
        )}

        <button
          onClick={submit}
          disabled={!file || submitting}
          className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition"
        >{submitting ? t("upload_in_progress") : t("upload_send")}</button>

        {error && <div className="text-sm text-rose-600">⚠ {error}</div>}

        {result && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-900">
            <div>{t("upload_success", {
              months: result.months_affected.length,
              managers: result.managers_count,
              rows: result.rows_inserted,
            })}</div>
            {result.months_skipped && result.months_skipped.length > 0 && (
              <div className="mt-1 text-amber-800">{t("upload_skipped", { months: result.months_skipped.join(", ") })}</div>
            )}
            {result.warnings.length > 0 && (
              <div className="mt-2">
                <div className="font-semibold">{t("upload_warnings_title")}</div>
                <ul className="list-disc pl-4 mt-1 space-y-0.5">
                  {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl p-4 border border-slate-200">
        <div className="text-sm font-semibold text-slate-800 mb-3">{t("upload_history_title")}</div>
        {loadingHistory && <div className="text-xs text-slate-500">…</div>}
        {!loadingHistory && history.length === 0 && <div className="text-xs text-slate-500">{t("upload_history_empty")}</div>}
        {history.length > 0 && (
          <ul className="space-y-2">
            {history.map(u => (
              <li key={u.id} className="text-xs border-b border-slate-100 pb-2 last:border-0">
                <div className="flex justify-between items-start gap-2">
                  <div className="font-medium text-slate-800 flex items-center gap-1.5 flex-wrap">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${u.source === "panel" ? "bg-sky-100 text-sky-800" : "bg-slate-100 text-slate-600"}`}>
                      {u.source === "panel" ? t("upload_src_panel") : t("upload_src_xlsx")}
                      {u.source === "panel" && u.version ? ` v${u.version}` : ""}
                    </span>
                    {u.source === "panel" && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${u.final ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                        {u.final ? t("upload_final") : t("upload_prelim")}
                      </span>
                    )}
                    {u.source !== "panel" && <span>{u.original_filename ?? "—"}</span>}
                  </div>
                  <div className="text-slate-400 whitespace-nowrap">{formatTs(u.uploaded_at)}</div>
                </div>
                <div className="text-slate-500 mt-0.5">
                  Месяцев: {u.months_affected.join(", ") || "—"} · Менеджеров: {u.managers_count ?? 0}
                  {u.warnings && u.warnings.length > 0 && (
                    <span className="text-amber-600"> · {t("upload_history_warns", { n: u.warnings.length })}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
